/**
 * TabPicker UI Toast Component
 * 
 * Toast notification component for displaying temporary messages.
 * Provides success, error, warning, and info variants with auto-dismiss.
 * 
 * @module TabPicker.UI.Toast
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast notification type
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast message interface
 */
export interface ToastMessage {
  id: string;
  message: string;
  type?: ToastType;
  duration?: number;
}

/**
 * Toast component props
 */
export interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
}

/**
 * Individual toast notification component
 * 
 * @param {ToastProps} props - Component props
 * @returns {JSX.Element} Toast element
 */
export function Toast({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose 
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade animation
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration, onClose]);
  
  const baseClasses = 'fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl px-4 py-3 text-sm shadow-lg transition-all duration-300';
  
  const typeClasses = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    warning: 'bg-amber-500 text-white',
    info: 'bg-white text-gray-700 border border-gray-200'
  };
  
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };
  
  return (
    <div 
      className={`${baseClasses} ${typeClasses[type]} ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{icons[type]}</span>
        <span>{message}</span>
      </div>
    </div>
  );
}

/**
 * Toast container component for managing multiple toasts
 */
export interface ToastContainerProps {
  toasts: ToastMessage[];
  removeToast: (id: string) => void;
}

/**
 * Toast container component
 * 
 * @param {ToastContainerProps} props - Component props
 * @returns {JSX.Element | null} Toast container element
 */
export function ToastContainer({ toasts, removeToast }: ToastContainerProps) {
  if (toasts.length === 0) return null;
  
  return createPortal(
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none">
      <div className="flex flex-col items-center gap-2 p-6">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className="pointer-events-auto"
            style={{
              transform: `translateY(-${index * 60}px)`
            }}
          >
            <Toast
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </div>,
    document.body
  );
}

/**
 * Hook for managing toast notifications
 * 
 * @returns Toast management functions and state
 * 
 * @example
 * const { toasts, addToast, removeToast } = useToast();
 * 
 * // Show a success toast
 * addToast('Game saved successfully!', 'success');
 * 
 * // Render toast container
 * <ToastContainer toasts={toasts} removeToast={removeToast} />
 */
export function useToast() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  
  const addToast = (
    message: string, 
    type: ToastType = 'info', 
    duration: number = 3000
  ) => {
    const id = Math.random().toString(36).slice(2, 10);
    const newToast: ToastMessage = { id, message, type, duration };
    setToasts(prev => [...prev, newToast]);
    return id;
  };
  
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };
  
  const clearToasts = () => {
    setToasts([]);
  };
  
  return {
    toasts,
    addToast,
    removeToast,
    clearToasts
  };
}